import React, { useState, useEffect, useRef } from 'react';
import { 
  ArrowDown, 
  PaperPlaneRight,
  BookBookmark,
  Mosque,
  BookOpen,
  GraduationCap,
  Star,
  Moon,
  Sun,
  Heart,
  Sparkle,
  Books
} from '@phosphor-icons/react';
import './HeroSection.css';
import TextScramble from '../components/common/TextScramble';

// Import hero background videos
import heroVideo1 from '../assets/images/hero/hero-video-back/0001.webm';
import heroVideo2 from '../assets/images/hero/hero-video-back/0002.webm';
import heroVideo3 from '../assets/images/hero/hero-video-back/0003.webm';

const heroVideos = [heroVideo1, heroVideo2, heroVideo3];

const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  // Interval untuk ganti video setiap 10 detik
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCurrentVideoIndex((prev) => (prev + 1) % heroVideos.length);
    }, 10000); // 10 detik per video
    
    return () => clearInterval(intervalRef.current);
  }, []);

  return (
    <section className="hero-section">
      {/* Full Section Video Background - Rounded Square Frame */}
      <div className="hero-video-frame">
        <div className="hero-video-wrapper">
          {heroVideos.map((video, index) => (
            <video
              key={index}
              src={video}
              autoPlay
              muted
              loop
              playsInline
              className={`hero-video ${index === currentVideoIndex ? 'active' : ''}`}
            />
          ))}
          {/* Overlay for text readability */}
          <div className="hero-video-overlay"></div>
        </div>
      </div>

      {/* Background Decorations */}
      <div className="hero-bg-decorations">
        {/* Large Circle Gradient dengan Pulse */}
        <div className="hero-bg-circle circle-1"></div>
        <div className="hero-bg-circle circle-2"></div>
        <div className="hero-bg-circle circle-3"></div>
      </div>

      {/* Subtle grid pattern */}
      <div className="hero-grid-pattern" />

      {/* Floating Phosphor Icons - Decorative */}
      <div className={`hero-icon-float icon-mosque ${isVisible ? 'animate-in' : ''}`}>
        <Mosque weight="duotone" />
      </div>
      <div className={`hero-icon-float icon-book ${isVisible ? 'animate-in' : ''}`}>
        <BookOpen weight="duotone" />
      </div>
      <div className={`hero-icon-float icon-graduation ${isVisible ? 'animate-in' : ''}`}>
        <GraduationCap weight="duotone" />
      </div>
      <div className={`hero-icon-float icon-books ${isVisible ? 'animate-in' : ''}`}>
        <Books weight="duotone" />
      </div>
      <div className={`hero-icon-float icon-star ${isVisible ? 'animate-in' : ''}`}>
        <Star weight="fill" />
      </div>
      <div className={`hero-icon-float icon-moon ${isVisible ? 'animate-in' : ''}`}>
        <Moon weight="fill" />
      </div>
      <div className={`hero-icon-float icon-sun ${isVisible ? 'animate-in' : ''}`}>
        <Sun weight="fill" />
      </div>
      <div className={`hero-icon-float icon-heart ${isVisible ? 'animate-in' : ''}`}>
        <Heart weight="fill" />
      </div>
      <div className={`hero-icon-float icon-sparkle ${isVisible ? 'animate-in' : ''}`}>
        <Sparkle weight="fill" />
      </div>

      {/* Main Content */}
      <div className="hero-content">
        <div className="hero-grid">
          {/* Left Column - Main Title */}
          <div className="hero-left">
            <h1 className="hero-title">
              <span className="hero-title-line" style={{'--line-index': 0}}>
                <TextScramble 
                  text="PONDOK" 
                  delay={800} 
                  duration={1200}
                  entranceAnimation="fadeSlide"
                />
              </span>
              <span className="hero-title-line hero-title-muted" style={{'--line-index': 1}}>
                <TextScramble 
                  text="PESANTREN" 
                  delay={1100} 
                  duration={1200}
                  entranceAnimation="fadeSlide"
                />
              </span>
              <span className="hero-title-line hero-title-main" style={{'--line-index': 2}}>
                <TextScramble 
                  texts={["BAHRUL", "ISLAMIC"]} 
                  delay={1500} 
                  duration={1500}
                  rotate={true}
                  rotateInterval={4000}
                  entranceAnimation="fadeSlide"
                />
              </span>
              <span className="hero-title-line hero-title-light" style={{'--line-index': 3}}>
                <TextScramble 
                  texts={["ULUM", "CENTRE"]} 
                  delay={1800} 
                  duration={1500}
                  rotate={true}
                  rotateInterval={4000}
                  entranceAnimation="fadeSlide"
                />
              </span>
            </h1>
          </div>

          {/* Right Column - Description & CTA */}
          <div className={`hero-right ${isVisible ? 'animate-in' : ''}`}>
            <p className="hero-description">
              Membentuk generasi unggul yang cerdas, berdaya saing, dan berakhlak 
              mulia melalui pendidikan Islami terpadu.
            </p>

            <div className="hero-buttons">
              <a href="/admission" className="btn btn-primary btn-lg">
                <PaperPlaneRight weight="bold" />
                Daftar Sekarang
              </a>
              <a href="#programs" className="btn btn-outline btn-lg">
                <BookBookmark weight="bold" />
                Lihat Program
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar - Scroll Indicator Only */}
      <div className={`hero-bottom ${isVisible ? 'animate-in' : ''}`}>
        <div className="hero-bottom-content">
          {/* Scroll Indicator */}
          <div className="hero-scroll">
            <span className="hero-scroll-text">Scroll</span>
            <ArrowDown weight="bold" className="hero-scroll-icon" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
